import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import routerEvents from 'next-router-events';
import useScreenSize from '../../../hooks/useScreenSize';

import HeaderMenu from '../../02_molecules/Header/HeaderMenu';
import HeaderDropdownNavigation from '../../02_molecules/Header/HeaderDropdown/HeaderDropdownNavigation';
import HeaderDropdownSearch from '../../02_molecules/Header/HeaderDropdown/HeaderDropdownSearch';

import styles from '../../02_molecules/Header/HeaderMenu/index.module.scss';

const Header = (props) => {
  const {
    display = '',
    navigationContents = [],
    searchContents = null,
    topLinks = [],
    stickyButtonActive = false,
    isStickyButtonMode = false,
    setStickyButtonMode = () => {},
  } = props;
  const createInitialToggled = (navigationContents) => {
    const toggled = {
      search: false,
      navigation: false,
    };

    navigationContents.forEach((nav) => {
      toggled[`navigation-${nav.slug}`] = false;
      toggled[`dropdown-navigation-${nav.slug}`] = false;
    });

    return toggled;
  };

  const [toggled, setToggled] = useState(() =>
    createInitialToggled(props.navigationContents ?? []),
  );

  const dropdownRef = useRef(null);
  const headerRef = useRef(null);
  const createdClickOutsideListener = useRef(false);
  const prevToggledRef = useRef(toggled);
  const toggledRef = useRef(toggled);
  toggledRef.current = toggled;
  const hasActiveMenu = Object.values(toggled).some((i) => i);

  const { width } = useScreenSize();

  useEffect(() => {
    if (width < 768 && stickyButtonActive && !hasActiveMenu && !isStickyButtonMode) {
      setStickyButtonMode(true);
    }

    if ((width >= 768 || !stickyButtonActive || hasActiveMenu) && isStickyButtonMode) {
      setStickyButtonMode(false);
    }
  }, [stickyButtonActive, width, isStickyButtonMode, hasActiveMenu]);

  const updateToggled = () => {
    setToggled((currentToggled) => {
      const newToggled = currentToggled;
      let needUpdate = false;
      Object.keys(newToggled).forEach((key) => {
        if (newToggled[key]) {
          newToggled[key] = false;
          needUpdate = true;
        }
      });

      if (needUpdate) {
        return { ...newToggled };
      }
      return currentToggled;
    });
  };
  const closeAllMenus = useCallback(() => {
    updateToggled();
  }, []);

  const focusTopMenuItem = useCallback(() => {
    const currentToggled = toggledRef.current;
    // Find opened menu item to be able to focus it.
    let openedMenu;
    Object.keys(currentToggled).forEach((key) => {
      if (currentToggled[key]) {
        openedMenu = key;
      }
    });

    // Focus menu item that was opened.
    let element;
    if (openedMenu === 'search') {
      element = document.querySelector('.header-menu-desktop-search--link');
    } else {
      element = document.querySelector('a.header-menu-desktop--link.toggled');
    }
    if (element) {
      element.focus();
    }
  }, []);

  const handleClickOutside = useCallback((event) => {
    const refsExist = dropdownRef.current && headerRef.current;

    const isClickToSearchIcon =
      (event.target &&
        event.target.classList &&
        event.target.classList.contains('header-menu-search--link')) ||
      (event.target.parentNode &&
        event.target.parentNode.classList &&
        event.target.parentNode.classList.contains('header-menu-search--link'));

    const isClickInsideDropdown = refsExist && dropdownRef.current.contains(event.target);

    const isClickToTopMenuItem =
      refsExist &&
      headerRef.current.querySelector('.header-container .header-menu .left') &&
      headerRef.current
        .querySelector('.header-container .header-menu .left')
        .contains(event.target);

    if (!isClickToSearchIcon && !isClickInsideDropdown && !isClickToTopMenuItem) {
      updateToggled();
    }
  }, []);

  const handleEsc = useCallback(
    (event) => {
      if (event.keyCode == 27) {
        focusTopMenuItem();
        closeAllMenus();
      }
    },
    [closeAllMenus, focusTopMenuItem],
  );

  const onRouterCompleteToggle = closeAllMenus;

  useEffect(() => {
    // The mobile menus stay toggled on page change, so force a reset.
    routerEvents.on('routeChangeComplete', onRouterCompleteToggle);
    document.addEventListener('keydown', handleEsc);

    return () => {
      routerEvents.off('routeChangeComplete', onRouterCompleteToggle);
      if (createdClickOutsideListener.current) {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEsc);
        createdClickOutsideListener.current = false;
      }
    };
  }, [handleClickOutside, handleEsc, onRouterCompleteToggle]);

  useEffect(() => {
    const prevToggled = prevToggledRef.current;

    document.addEventListener('mousedown', handleClickOutside);
    createdClickOutsideListener.current = true;

    // Delete listener if mobile navigation or search popup is closed.
    if (
      ((prevToggled.search && !toggled.search) ||
        (prevToggled.navigation && !toggled.navigation)) &&
      createdClickOutsideListener.current
    ) {
      document.removeEventListener('mousedown', handleClickOutside);
      createdClickOutsideListener.current = false;
    }

    prevToggledRef.current = toggled;
  });

  return (
    <>
      <header
        className={`site-header ${styles['site-header']} ${display} ${styles[display]}`}
        ref={headerRef}
      >
        <div className={`d-lg-none `}>
          <HeaderMenu
            device="mobile"
            onMenuToggle={(toggles) => setToggled(toggles)}
            {...props}
            toggled={toggled}
          />
        </div>

        <div className="d-none d-lg-block">
          <HeaderMenu
            device="desktop"
            onMenuToggle={(toggles) => setToggled(toggles)}
            contents={navigationContents}
            dropdownRef={dropdownRef}
            {...props}
            toggled={toggled}
          />
        </div>
      </header>

      {display === 'full' && (
        <div ref={dropdownRef}>
          <div className="d-lg-none">
            <HeaderDropdownNavigation
              device="mobile"
              toggled={toggled.navigation}
              onMenuToggle={(navToggled) =>
                setToggled({
                  ...toggled,
                  navigation: navToggled,
                })
              }
              contents={navigationContents.concat(topLinks)}
            />
          </div>

          <div className="d-none d-lg-block">
            {navigationContents.map((nav) => (
              <HeaderDropdownNavigation
                key={`dropdown-${nav.slug}`}
                device="desktop"
                toggled={toggled[`navigation-${nav.slug}`]}
                focusTopMenuItem={focusTopMenuItem}
                contents={nav.columns}
                cta={nav.cta}
              />
            ))}
          </div>
          <HeaderDropdownSearch
            toggled={toggled.search}
            focusTopMenuItem={focusTopMenuItem}
            searchContents={{ ...searchContents }}
            onSearch={() => setToggled({ ...toggled, search: false })}
          />
        </div>
      )}
    </>
  );
};

const propTypes = {
  display: PropTypes.string,
  isCheckoutHeader: PropTypes.bool,
  navigationContents: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      slug: PropTypes.string,
      cta: PropTypes.shape({
        src: PropTypes.string,
        label: PropTypes.string,
        button: PropTypes.shape({
          type: PropTypes.string,
          label: PropTypes.string,
        }),
      }),
      columns: PropTypes.arrayOf(
        PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string,
            url: PropTypes.string,
          }),
        ),
      ),
    }),
  ),
  topLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      url: PropTypes.string,
    }),
  ),
  searchContents: PropTypes.shape({
    suggested: PropTypes.shape({
      title: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string,
          nextLink: PropTypes.shape({
            href: PropTypes.string,
            as: PropTypes.string,
          }),
        }),
      ),
    }),
    onGotoSearch: PropTypes.func,
    inputValidators: PropTypes.arrayOf(PropTypes.func),
  }),
  stickyButtonActive: PropTypes.bool,
  isStickyButtonMode: PropTypes.bool,
  setStickyButtonMode: PropTypes.func,
};

Header.propTypes = propTypes;

export { propTypes };

export default Header;
